import { z } from 'zod';
export const HAIR_COLORS = [
    { id: 'brown', name: 'Brun', hex: '#58422f' },
    { id: 'black', name: 'Noir', hex: '#2a1c1a' },
    { id: 'chestnut', name: 'Châtain', hex: '#81442e' },
    { id: 'blond', name: 'Blond', hex: '#ffc82e' },
    { id: 'red', name: 'Roux', hex: '#bd3644' },
    { id: 'ash', name: 'Cendré', hex: '#a9a9b8' },
];
export const EYE_COLORS = [
    { id: 'blue', name: 'Bleu', hex: '#498ace' },
    { id: 'green', name: 'Vert', hex: '#579816' },
    { id: 'brown', name: 'Brun', hex: '#58422f' },
    { id: 'grey', name: 'Gris', hex: '#6b6b7c' },
    { id: 'red', name: 'Rouge', hex: '#bd3644' },
    { id: 'gold', name: 'Doré', hex: '#ffc82e' },
];
/** Multipliers applied to the warrior's extracted skin layer. White preserves the reference art. */
export const SKIN_COLORS = [
    { id: 'light', name: 'Claire', hex: '#ffffff', swatch: '#fdd1bb' },
    { id: 'warm', name: 'Dorée', hex: '#e7b485', swatch: '#d9a079' },
    { id: 'brown', name: 'Brune', hex: '#bb8065', swatch: '#a96a58' },
    { id: 'deep', name: 'Foncée', hex: '#865b50', swatch: '#78473f' },
];
const hairIds = HAIR_COLORS.map((color) => color.id);
const eyeIds = EYE_COLORS.map((color) => color.id);
/**
 * Each style but the shaved skull is one rig feature: a greyscale silhouette
 * drawn once on the bald reference and tinted like any hair. `hair` is the
 * first style's feature, kept under its old name so nothing already built moves.
 */
export const HAIR_FEATURES = [
    'hair',
    'hair-long',
    'hair-ponytail',
    'hair-braid',
    'hair-bob',
    'hair-curly',
];
export const HAIR_STYLES = [
    { id: 'short', name: 'Cheveux courts', feature: 'hair' },
    { id: 'long', name: 'Cheveux longs', feature: 'hair-long' },
    { id: 'ponytail', name: 'Queue de cheval', feature: 'hair-ponytail' },
    { id: 'braid', name: 'Tresse', feature: 'hair-braid' },
    { id: 'bob', name: 'Carré', feature: 'hair-bob' },
    { id: 'curly', name: 'Boucles', feature: 'hair-curly' },
    { id: 'shaved', name: 'Crâne rasé', feature: null },
];
const hairStyleIds = HAIR_STYLES.map((style) => style.id);
/** The feature that draws a style, or null for a shaved skull (nothing drawn). */
export function hairFeatureOf(style) {
    const known = HAIR_STYLES.find((item) => item.id === style);
    return known === undefined ? 'hair' : known.feature;
}
/**
 * Two bodies on one silhouette: the feminine one is the reference redrawn
 * inside its exact outline (a chest wrap, softer eyes), so every piece of
 * equipment fits both without being drawn twice. `variant` names its frames
 * (`body-female`, `skin-female`, written by `build:rig --variant female`).
 */
export const BODY_SHAPES = [
    { id: 'male', name: 'Masculin', variant: null },
    { id: 'female', name: 'Féminin', variant: 'female' },
];
const bodyIds = BODY_SHAPES.map((shape) => shape.id);
/** The frames a body or skin layer plays for this body: `body`, `body-female`… */
export function bodyLayerOf(layer, body) {
    const variant = BODY_SHAPES.find((shape) => shape.id === body)?.variant ?? null;
    return variant === null ? layer : `${layer}-${variant}`;
}
export const DEFAULT_LOOK = {
    hair: 'brown',
    eyes: 'blue',
    hairStyle: 'short',
    skin: 'light',
    body: 'male',
};
/**
 * Unknown names fall back to the defaults rather than failing: a colour
 * removed from the list must not lock a character out of the game.
 */
export const LookSchema = z.object({
    hair: z.enum(hairIds).catch(DEFAULT_LOOK.hair),
    eyes: z.enum(eyeIds).catch(DEFAULT_LOOK.eyes),
    hairStyle: z
        .enum(hairStyleIds)
        .default(DEFAULT_LOOK.hairStyle)
        .catch(DEFAULT_LOOK.hairStyle),
    skin: z
        .enum(['light', 'warm', 'brown', 'deep'])
        .default(DEFAULT_LOOK.skin)
        .catch(DEFAULT_LOOK.skin),
    body: z.enum(bodyIds).default(DEFAULT_LOOK.body).catch(DEFAULT_LOOK.body),
});
export function hairHex(id) {
    return HAIR_COLORS.find((color) => color.id === id)?.hex ?? hairHex(DEFAULT_LOOK.hair);
}
export function eyeHex(id) {
    return EYE_COLORS.find((color) => color.id === id)?.hex ?? eyeHex(DEFAULT_LOOK.eyes);
}
export function skinHex(id) {
    return SKIN_COLORS.find((color) => color.id === id)?.hex ?? skinHex(DEFAULT_LOOK.skin);
}
/** The tints as the client's layers want them: by layer name, as CSS hex. */
export function lookTints(look) {
    return { hair: hairHex(look.hair), eyes: eyeHex(look.eyes), skin: skinHex(look.skin) };
}
//# sourceMappingURL=look.js.map